// src/hooks/useAvatarCache.ts
import { useEffect, useState, useCallback } from 'react';

const AVATAR_CACHE_DB = 'orifoliAvatarCache';
const AVATAR_CACHE_STORE = 'avatars';
const CACHE_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7日間

const openAvatarCacheDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(AVATAR_CACHE_DB, 1);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(AVATAR_CACHE_STORE)) {
        db.createObjectStore(AVATAR_CACHE_STORE, { keyPath: 'url' });
      }
    };
  });
};

const getCachedAvatar = async (url: string): Promise<string | null> => {
  try {
    const db = await openAvatarCacheDB();
    return new Promise((resolve) => {
      const tx = db.transaction(AVATAR_CACHE_STORE, 'readonly');
      const store = tx.objectStore(AVATAR_CACHE_STORE);
      const request = store.get(url);
      request.onsuccess = () => {
        const data = request.result;
        if (data && Date.now() - data.timestamp < CACHE_EXPIRY) {
          resolve(data.dataUrl);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
};

const cacheAvatar = async (url: string, dataUrl: string): Promise<void> => {
  try {
    const db = await openAvatarCacheDB();
    const tx = db.transaction(AVATAR_CACHE_STORE, 'readwrite');
    const store = tx.objectStore(AVATAR_CACHE_STORE);
    store.put({ url, dataUrl, timestamp: Date.now() });
  } catch {
    // キャッシュ失敗は無視
  }
};

/**
 * アバター画像をIndexedDBにキャッシュするフック
 * XOR画像のfetchを優先させるため、100ms遅延して読み込む
 */
export function useAvatarCache(src: string | undefined): string | null {
  const [cachedSrc, setCachedSrc] = useState<string | null>(null);

  const loadAndCacheAvatar = useCallback(async (url: string) => {
    // 1. キャッシュを確認
    const cached = await getCachedAvatar(url);
    if (cached) {
      setCachedSrc(cached);
      return;
    }

    // 2. フェッチしてキャッシュ
    try {
      const response = await fetch(url);
      if (!response.ok) {
        setCachedSrc(url); // フォールバック
        return;
      }
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        setCachedSrc(dataUrl);
        cacheAvatar(url, dataUrl);
      };
      reader.readAsDataURL(blob);
    } catch {
      setCachedSrc(url); // フォールバック
    }
  }, []);

  useEffect(() => {
    if (src) {
      // XOR画像のfetchを優先させるため、遅延して読み込む
      const timer = setTimeout(() => {
        loadAndCacheAvatar(src);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setCachedSrc(null);
    }
  }, [src, loadAndCacheAvatar]);

  return cachedSrc;
}

export default useAvatarCache;
